import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Box, Paper, Tooltip, Typography } from '@mui/material'
import { NewTable } from '../../../../../components/datatable/NewTable'
import { AlignedHeadlessTableBox, AlignedTableBox } from '../../../AlignedTableWrappers'
import i18n from 'simple-react-i18n'
import { isEqual, isNil, keyBy } from 'lodash'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Select from '../../../../../components/forms/Select'
import IndicatorDialogLayout from '../IndicatorDialogLayout'
import { InputTable } from '../IndicatorTable'
import RpqsAction from '../../../../actions/RpqsAction'
import { formatIndicatorValue, getStatusFromCode } from '../../../../utils/RPQSUtils'
import { computeP103_2BValues, getP103_2BLocks, getP103_2BPointsLabel, P103_2B_CODES, P103_2B_PARTS } from '../../../../utils/P103_2BUtils'
import { rpqsIndicatorShape } from '../../../../constants/RpqsShapes'
import { FIELD_TYPE_BOOL, GREY_BG } from '../../../../constants/RpqsConstant'

const DISABLED_COLOR = 'rgba(0, 0, 0, 0.38)'

const booleanOptions = () => [{ code: 'true', name: i18n.yes }, { code: 'false', name: i18n.no }]

const getBooleanLabel = value => {
    if (`${value}` === 'true') return i18n.yes
    if (`${value}` === 'false') return i18n.no
    return ''
}

const getHeaders = year => [
    { key: 'code', value: i18n.indicator, width: '8%', noSort: true },
    { key: 'name', value: i18n.indicatorDenomination, width: '44%', noSort: true },
    { key: 'points', value: i18n.points, width: '8%', noSort: true },
    { key: 'previousValue', value: `${year - 1}`, width: '10%', noSort: true },
    { key: 'value', value: `${year}`, width: '13%', noSort: true },
    { key: 'unit', value: i18n.unit, width: '17%', noSort: true },
]
const TABLE_ROWS_PER_PAGE = [{ value: 1000, label: '1000' }]
const TABLE_PAPER_SX = { padding: '0px 10px' }

const getParts = () => [
    { key: 'A', title: i18n.p103PartA, codes: P103_2B_PARTS.A },
    { key: 'B', title: i18n.p103PartB, codes: P103_2B_PARTS.B, lockedMessage: i18n.p103PartBLocked },
    { key: 'C', title: i18n.p103PartC, codes: P103_2B_PARTS.C, lockedMessage: i18n.p103PartCLocked },
]

const RpqsIndicatorDialogP103_2B = ({
    open = false,
    setOpen = () => {},
    indicators = [],
    rpqs = {},
    egId,
    onSaved = () => {},
}) => {
    const dispatch = useDispatch()
    const [values, setValues] = useState({})
    const [initialValues, setInitialValues] = useState({})
    const [notes, setNotes] = useState('')

    const year = rpqs.year
    const mainCode = P103_2B_CODES.MAIN

    const groupByCode = useMemo(() => keyBy(indicators.filter(ind => ind.code === mainCode || ind.parent === mainCode), 'code'), [indicators])
    const main = groupByCode[mainCode]

    useEffect(() => {
        if (!open) return
        const initial = Object.fromEntries(Object.values(groupByCode).map(ind => [ind.code, ind.value ?? '']))
        const initialComputed = computeP103_2BValues(initial, initial)
        setValues(initialComputed)
        setInitialValues(initialComputed)
        setNotes(main?.notes ?? '')
    }, [open])

    const { lockedB, lockedC } = getP103_2BLocks(values)
    const lockedByPart = { A: false, B: lockedB, C: lockedC }

    const handleChangeValue = (code, value) => setValues(prev => computeP103_2BValues({ ...prev, [code]: value }, prev))

    const handleRegister = () => {
        const body = {
            year: rpqs.year,
            collectivityId: rpqs.collectivityId,
            managementUnitId: parseInt(egId, 10),
            mainCode,
            notes,
            indicators: Object.values(groupByCode).map(ind => ({
                code: ind.code,
                value: values[ind.code] ?? '',
                manualInput: !!ind.manualInput,
            })),
        }
        dispatch(RpqsAction.updateIndicators(body)).then(json => {
            if (json?.update) {
                onSaved()
                setOpen(false)
            }
        })
    }

    const valueField = (ind, readMode) => (ind.fieldType === FIELD_TYPE_BOOL ? (
        <Select
            value={values[ind.code] ?? ''}
            options={booleanOptions()}
            onChange={v => handleChangeValue(ind.code, v ?? '')}
            readMode={readMode}
        />
    ) : (
        <InputTable
            value={values[ind.code] ?? ''}
            onChange={v => handleChangeValue(ind.code, v)}
            readMode={readMode}
            fieldType={ind.fieldType}
        />
    ))

    const previousValueOf = ind => (ind.fieldType === FIELD_TYPE_BOOL ? getBooleanLabel(ind.previousValue) : formatIndicatorValue(ind.previousValue ?? '', ind.fieldType))

    const tableRow = (ind, locked, greyed = locked) => {
        const cell = value => ({ value, fontColor: greyed ? DISABLED_COLOR : 'black' })
        return {
            code: cell(ind.code.startsWith('INT.') ? '' : ind.code),
            name: cell(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textWrap: 'wrap' }}>
                    {ind.name}
                    {ind.comment && (
                        <Tooltip title={ind.comment}>
                            <InfoOutlinedIcon fontSize='small' color='action'/>
                        </Tooltip>
                    )}
                </Box>,
            ),
            points: cell(getP103_2BPointsLabel(ind.code, values)),
            previousValue: cell(previousValueOf(ind)),
            value: cell(valueField(ind, ind.readOnly || locked)),
            unit: cell(ind.unit),
        }
    }

    const getRows = (codes, locked) => codes.filter(code => !isNil(groupByCode[code])).map(code => tableRow(groupByCode[code], locked))

    const networkLength = groupByCode[P103_2B_CODES.NETWORK_LENGTH]
    const headers = getHeaders(year)

    return (
        <IndicatorDialogLayout
            open={open}
            title={main ? `${main.code} - ${main.mainName || main.name}` : ''}
            titleActions={[{ icon: 'close', tooltip: i18n.close, onClick: () => setOpen(false) }]}
            notes={notes}
            onChangeNotes={setNotes}
            status={getStatusFromCode(main?.status)}
            onRegister={handleRegister}
            hasChanges={!isEqual(values, initialValues)}
            footer={main && (
                <Paper sx={TABLE_PAPER_SX}>
                    <NewTable
                        headers={headers}
                        rows={[tableRow(main, true, false)]}
                        rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                        WrapperComponent={AlignedHeadlessTableBox}
                        disableLocalStorage
                    />
                </Paper>
            )}
        >
            <Paper sx={TABLE_PAPER_SX}>
                <NewTable
                    headers={headers}
                    rows={networkLength ? [tableRow(networkLength, false)] : []}
                    rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                    WrapperComponent={AlignedTableBox}
                    disableLocalStorage
                />
                {getParts().map(part => (
                    <React.Fragment key={part.key}>
                        <Box sx={{ backgroundColor: GREY_BG, padding: 1 }}>
                            <Typography fontWeight='bold' sx={{ color: lockedByPart[part.key] ? DISABLED_COLOR : undefined }}>{part.title}</Typography>
                            {lockedByPart[part.key] && part.lockedMessage && (
                                <Typography variant='body2' color='error'>{part.lockedMessage}</Typography>
                            )}
                        </Box>
                        <NewTable
                            headers={headers}
                            rows={getRows(part.codes, lockedByPart[part.key])}
                            rowsPerPageOptions={TABLE_ROWS_PER_PAGE}
                            WrapperComponent={AlignedHeadlessTableBox}
                            disableLocalStorage
                        />
                    </React.Fragment>
                ))}
            </Paper>
        </IndicatorDialogLayout>
    )
}

RpqsIndicatorDialogP103_2B.propTypes = {
    open: PropTypes.bool,
    setOpen: PropTypes.func,
    indicators: PropTypes.arrayOf(rpqsIndicatorShape),
    rpqs: PropTypes.shape({ year: PropTypes.number, collectivityId: PropTypes.number }),
    egId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSaved: PropTypes.func,
}

export default RpqsIndicatorDialogP103_2B
